/**
 * deliveryOutbox — durable on-device storage for delivery confirmations that
 * could not be sent (no/low signal at the doorstep).
 *
 * Unlike deliveryDraftStore (in-memory, lost on reload), this uses IndexedDB
 * so a saved confirmation survives the app being fully closed and reopened.
 * It holds the *frozen payload* captured at the doorstep — including the
 * Blob/File media and the timestamp + GPS as they were at delivery — to be
 * retained without replay. These older records cannot prove exact acceptance.
 *
 * IndexedDB stores Blob/File natively (structured clone), so media is kept as
 * raw blobs rather than base64 — no encoding round-trip.
 *
 * Record shape (one per saved confirmation, keyed by orderId):
 *   {
 *     orderId,                  // string — key
 *     receivedBy,               // string
 *     receivedByRelationship,   // string
 *     notes,                    // string
 *     gpsLatitude,              // number | null  (frozen at doorstep)
 *     gpsLongitude,             // number | null
 *     gpsAccuracy,              // number | null
 *     deliveryTime,             // string | null  — frozen ISO; the delivery
 *                               //   time we replay, NOT the resend time
 *     signature,                // Blob | null    (PNG)
 *     photos,                   // Array<Blob|File>
 *     video,                    // Blob|File | null
 *     coreSent,                 // boolean — whether the core confirm already
 *                               //   reached the server (video may still be
 *                               //   pending). Historical, not receipt evidence.
 *     savedAt,                  // string ISO — when it was saved to device
 *   }
 */

import { withDeliveryCopyOperation } from './deliveryCopyOperation';
import { readPhotoOrderHold } from './deliveryPhotoDraftPersistence';
import { hasPreparedDeliveryWorkspace } from './deliveryDraftStore';

const DB_NAME = 'lvb-delivery-outbox';
const DB_VERSION = 1;
const STORE = 'confirmations';

let dbPromise = null;

const changedCopy = () => new Error('This saved copy changed. Refresh saved deliveries and review it again.');
const newRevision = () => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

// Legacy records have no principal/attempt identity. Any prepared copy or
// retained attempt evidence blocks this separate writer, including feature-off
// callers. The shared lock spans admission and the caller's whole operation.
export async function withLegacyDeliveryOperation(orderId, action, { context, isCurrent = () => true } = {}) {
  return withDeliveryCopyOperation(orderId, async (ownedContext) => {
    if (!isCurrent()) throw new Error('The delivery opening changed. Nothing was sent or changed.');
    if (hasPreparedDeliveryWorkspace(orderId)) throw new Error('Other photo work remains in this tab. Keep it open and review that work; nothing was sent or changed.');
    const hold = await readPhotoOrderHold(orderId);
    if (!hold || typeof hold.held !== 'boolean') throw new Error('Saved work could not be checked. Nothing was sent or changed.');
    if (hold.held) throw new Error('Other saved photo work exists for this order. Review saved work; nothing was sent or changed.');
    return withDeliveryCopyOperation(orderId, () => {
      if (!isCurrent()) throw new Error('The delivery opening changed. Nothing was sent or changed.');
      if (hasPreparedDeliveryWorkspace(orderId)) throw new Error('Other photo work remains in this tab. Keep it open and review that work; nothing was sent or changed.');
      return action(ownedContext);
    }, { context: ownedContext, requireCrossContext: true });
  }, { context, requireCrossContext: true });
}

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'orderId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  dbPromise = dbPromise.catch((error) => { dbPromise = null; throw error; });
  return dbPromise;
}

// Wrap an IDBRequest in a promise.
function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Save a new confirmation without replacing existing evidence. Callers
 * must surface that honestly, never report a false "saved".
 */
export async function saveDelivery(record, { context, isCurrent = () => true } = {}) {
  return withLegacyDeliveryOperation(record?.orderId, () => mutate(record.orderId, (existing) => {
    if (existing !== undefined) {
      throw Object.assign(new Error('Another saved copy already exists for this order. It was not replaced. Your current work is still in this form.'),
        { code: 'LEGACY_COPY_EXISTS' });
    }
    return { value: { ...record, localRevision: newRevision(), savedAt: record.savedAt ?? new Date().toISOString() } };
  }, isCurrent), { context, isCurrent });
}

// Read, compare and mutate in ONE transaction. Only completion acknowledges a
// write/delete; request success can still be followed by transaction abort.
async function mutate(orderId, decide, isCurrent = () => true) {
  const db = await openDB();
  if (!isCurrent()) throw new Error('The selection is no longer active. Nothing was deleted.');
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    let result; let failure;
    tx.oncomplete = () => resolve(result);
    tx.onerror = (event) => reject(failure || tx.error || event.target?.error || new Error('Saved-copy storage failed'));
    tx.onabort = () => reject(failure || tx.error || new Error('Saved-copy operation aborted'));
    const read = store.get(orderId);
    read.onsuccess = () => {
      try {
        if (!isCurrent()) throw new Error('The selection is no longer active. Nothing was deleted.');
        const decision = decide(read.result);
        result = decision.result;
        if (decision.value) store.put(decision.value);
        if (decision.remove) store.delete(orderId);
      } catch (error) { failure = error; tx.abort(); }
    };
  });
}

/** Get one saved confirmation by order id, or null if none. */
export async function getDelivery(orderId) {
  const db = await openDB();
  const tx = db.transaction(STORE, 'readonly');
  const result = await promisifyRequest(tx.objectStore(STORE).get(orderId));
  return result ?? null;
}

/** Presence only: never read another account's unowned legacy media to admit work. */
export async function hasLegacyDelivery(orderId) {
  if (typeof orderId !== 'string' || !orderId.length) throw new Error('This saved copy has no usable local key.');
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    let present = false;
    tx.oncomplete = () => resolve(present);
    tx.onerror = () => reject(tx.error || new Error('Saved-copy presence could not be checked.'));
    tx.onabort = () => reject(tx.error || new Error('Saved-copy presence check aborted.'));
    const request = tx.objectStore(STORE).getKey(orderId);
    request.onsuccess = () => { present = request.result !== undefined; };
  });
}

/** Get every saved confirmation (for the blue-stop / unsent-banner views). */
export async function getAllDeliveries() {
  const db = await openDB();
  const tx = db.transaction(STORE, 'readonly');
  const result = await promisifyRequest(tx.objectStore(STORE).getAll());
  return result ?? [];
}

async function prepareRecord(orderId, isCurrent) {
  return mutate(orderId, (record) => {
    if (!record) return { result: null };
    if (typeof record.localRevision === 'string' && record.localRevision) return { result: record };
    const value = { ...record, localRevision: newRevision() };
    return { value, result: value };
  }, isCurrent);
}

export async function prepareDeliveryDeletion(orderId, isCurrent, { context } = {}) {
  return withLegacyDeliveryOperation(orderId, async () => {
    const record = await prepareRecord(orderId, isCurrent);
    return record ? { orderId, localRevision: record.localRevision } : null;
  }, { context, isCurrent });
}

export async function deleteSavedDelivery(selection, isCurrent, { context } = {}) {
  return withLegacyDeliveryOperation(selection?.orderId, () => mutate(selection.orderId, (record) => {
    if (!record) return { result: 'absent' };
    if (!selection.localRevision || record.localRevision !== selection.localRevision) throw changedCopy();
    return { remove: true, result: 'deleted' };
  }, isCurrent), { context, isCurrent });
}
